/** @file guideBriefOf: the brief asking an image model to paint the character over the guide sheets. */
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { ArtBriefProfile } from '../artBriefOf';
import { briefCamera } from '../briefCamera';
import { facingWords } from '../facingWords';
import { partName } from '../partName';
import { guideSheetOf } from './guideSheetOf';
import { restPlacementOf } from './restPlacementOf';
import { SHEET_FACINGS } from './sheetFacings';
import type { PlacedPart } from './sheetTypes';

const INTRO = [
  '# Art brief: paint over the guide sheets',
  '',
  'Each facing below comes with a guide image (`guide.<facing>.png`, saved from the Art screen with',
  '`Guide sheet`): the character in its rest pose, every part a flat coloured region with a black',
  'outline, white dots on the joints, and a legend strip at the bottom.',
  '',
  'Paint the character over this guide at exactly this size and position, one part per coloured',
  "region, keep the regions' extents, do not move the joints; return the painted image without the",
  'guide colours.',
  '',
  '- Return an image of the guide\'s exact size; keep the figure where the guide has it.',
  '- Fill each region with the part the legend names; a part may stop short of its region but never',
  '  spill into another region or far past the outline.',
  '- The white dots are joints: the parts meet there; keep each part reaching its dot.',
  '- Everything outside the regions stays transparent. The legend strip may stay as it is.',
  "- Left and right are the character's own. Save the result as `painted.<facing>.png`.",
];

/** One facing: heading with both file names and the size, then the legend lines (colour, drawable, words). */
function facingSection(puppet: Puppet, deg: number, label: string): string[] {
  const guide = guideSheetOf(restPlacementOf(puppet, deg));
  const { width, height } = guide.image;
  return [`## \`guide.${String(deg)}.png\` -> \`painted.${String(deg)}.png\`: ${label}, ${facingWords(deg)}`, '',
    `Size ${String(width)}x${String(height)} px (the figure is the top ${String(guide.figureHeight)} px). Regions:`, '',
    ...guide.regions.map((r) => `- ${r.hex} \`${r.part.drawable}\`: ${regionWords(r.part, guide.placement.parts)}`)];
}

/** The part's name; a node with layered drawables reads `head: hat, face, hair back as one piece`. */
function regionWords(part: PlacedPart, parts: readonly PlacedPart[]): string {
  const layers = parts.filter((p) => p.node === part.node);
  return layers.length > 1 ? `${partName(part.node)}: ${layers.map((p) => partName(p.drawable)).join(', ')} as one piece` : partName(part.drawable);
}

/**
 * The guided-overpaint brief: intro with the paint-over instruction, style, camera, then one section per facing of
 * `SHEET_FACINGS` with the guide and result file names, the guide's size and its legend (colour -> part).
 */
export function guideBriefOf(puppet: Puppet, style: string, profile: ArtBriefProfile): string {
  const lines = [...INTRO, '', '## Style', '', style.trim() === '' ? 'No style notes.' : style.trim(), '', ...briefCamera(profile)];
  for (const f of SHEET_FACINGS) lines.push('', ...facingSection(puppet, f.deg, f.label));
  return [...lines, ''].join('\n');
}
